import { BorderRadiusPrefix, BoxShadowPrefix, ColorPrefix, FontSizePrefix, FontWeightPrefix, LineHeightPrefix, SizingPrefix, SpacePrefix, normalizeVariable } from "../theming";
import { useMatchedBreakpoints } from "../BreakpointProvider";
import { useMemo } from "react";
import { parseResponsiveValue } from "../useResponsiveValue";
import { isNil } from "../../../shared";
/*
SYNTAX:

// No breakpoint, no pseudo, known value
<Button backgroundColor="sunray-10">Toto</Button>

// No breakpoint, no pseudo, dynamic value
<Button backgroundColor="#fff">Toto</Button>

// No breakpoint, pseudo, known value
<Button backgroundColorHover="sunray-10">Toto</Button>

// No breakpoint, pseudo, dynamic value
<Button backgroundColorHover="#fff">Toto</Button>

// Breakpoint, no pseudo, known value
<Button backgroundColor={{ s: "sunray-10", md: "primary-10", lg: "black" }}>Toto</Button>

// Breakpoint, no pseudo, dynamic value
<Button backgroundColor={{ s: "sunray-10", md: "#fff", lg: "black" }}>Toto</Button>

// Breakpoint, pseudo, known value
<Button backgroundColorHover={{ s: "sunray-10", md: "primary-10", lg: "black" }}>Toto</Button>

// Breakpoint, pseudo, dynamic value
<Button backgroundColorHover={{ s: "sunray-10", md: "#fff", lg: "black" }}>Toto</Button>
*/

const GlobalValues = ["inherit", "initial", "revert", "unset"];
const ColorExpressionTypes = ["#", "rgb", "rgba", "hsl", "hsla"];
const SizingScale = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const SpacingScale = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
const Colors = ["white", "black", // Marine
"marine-1", "marine-2", "marine-3", "marine-4", "marine-5", "marine-6", "marine-7", "marine-8", "marine-9", "marine-10", // Sunray
"sunray-1", "sunray-2", "sunray-3", "sunray-4", "sunray-5", "sunray-6", "sunray-7", "sunray-8", "sunray-9", "sunray-10", // Moonstone
"moonstone-1", "moonstone-2", "moonstone-3", "moonstone-4", "moonstone-5", "moonstone-6", "moonstone-7", "moonstone-8", "moonstone-9", "moonstone-10", // Cloud
"cloud-1", "cloud-2", "cloud-3", "cloud-4", "cloud-5", "cloud-6", "cloud-7", "cloud-8", "cloud-9", "cloud-10", // Neutral
"neutral-1", "neutral-2", "neutral-3", "neutral-4", "neutral-5", "neutral-6", "neutral-7", "neutral-8", "neutral-9", "neutral-10", // Neutral dark
"neutral-dark-1", "neutral-dark-2", "neutral-dark-3", "neutral-dark-4", "neutral-dark-5", "neutral-dark-6", "neutral-dark-7", "neutral-dark-8", "neutral-dark-9", "neutral-dark-10", // Beetle
"beetle-1", "beetle-2", "beetle-3", "beetle-4", "beetle-5", "beetle-6", "beetle-7", "beetle-8", "beetle-9", "beetle-10", // Botanic
"botanic-1", "botanic-2", "botanic-3", "botanic-4", "botanic-5", "botanic-6", "botanic-7", "botanic-8", "botanic-9", "botanic-10", // Primary
"primary-1", "primary-2", "primary-3", "primary-4", "primary-5", "primary-6", "primary-7", "primary-8", "primary-9", "primary-10"];
const BackgroundColorAliases = ["alias-1", "alias-2", "alias-3", "alias-4", "alias-5", "alias-6", "alias-info-1", "alias-negative-1", "alias-negative-2", "alias-positive-1", "alias-positive-2", "alias-primary-1", "alias-primary-2", "alias-warning-1", "alias-warning-2"];
const BorderWidthAndStyle = "1px solid";
const BorderColorAliases = ["alias-1", "alias-2", "alias-3", "alias-4", "alias-negative-1", "alias-negative-1-translucent", "alias-negative-2", "alias-positive-1", "alias-primary-1", "alias-primary-1-translucent", "alias-warning-1"];
const IconColorAliases = ["alias-1", "alias-2", "alias-info-1", "alias-negative-1", "alias-negative-2", "alias-positive-1", "alias-positive-2", "alias-primary-1", "alias-warning-1", "alias-warning-2"];
const TextColorAliases = ["alias-1", "alias-1-hover", "alias-1-active", "alias-2", "alias-2-hover", "alias-2-active", "alias-3", "alias-3-hover", "alias-3-active", "alias-4", "alias-primary-1", "alias-primary-1-hover", "alias-primary-1-active", "alias-negative-1", "alias-negative-1-hover", "alias-negative-1-active", "alias-negative-2", "alias-info-1", "alias-info-1-hover", "alias-info-1-active", "alias-positive-1", "alias-positive-1-hover", "alias-positive-1-active", "alias-positive-2", "alias-warning-1", "alias-warning-1-hover", "alias-warning-1-active", "alias-warning-2", "alias-input-selection", "alias-input-placeholder"];
const BorderRadiusScale = [0, 1, 2, 3, 4, "pill"];
const BoxShadowScale = [1, 2, 3, 4];
const BoxShadowAliases = ["alias-floating", "alias-lifted", "alias-raised", "alias-skim"];
const FontSizeScale = [1, 2, 3, 4, 5, 6, 7, 8, 9, "headline", "subheadline"];
const FontWeightScale = [1, 2, 3];
const LineHeightScale = [1, 2, 3, 4, 5, 6];

function createValuesMapping(values, template) {
  const mapping = {};
  values.reduce((acc, x) => {
    acc[x] = template(x);
    return acc;
  }, mapping);
  return mapping;
}

function composePrefixes(...rest) {
  return rest.reduce((acc, prefix) => {
    return !isNil(prefix) ? `${acc}${acc !== "" ? "-" : ""}${prefix}` : acc;
  }, "");
}

function createPrefixedValueTemplate(prefix) {
  return value => `var(${normalizeVariable(value, prefix)})`;
}

export const SpacingMapping = createValuesMapping(SpacingScale, createPrefixedValueTemplate(SpacePrefix));
export const SizingMapping = createValuesMapping(SizingScale, createPrefixedValueTemplate(SizingPrefix));
export const ColorMapping = createValuesMapping(Colors, createPrefixedValueTemplate(ColorPrefix));
export const BackgroundColorMapping = { ...createValuesMapping(BackgroundColorAliases, createPrefixedValueTemplate(composePrefixes(ColorPrefix, "bg"))),
  ...ColorMapping
};
export const BorderMapping = { ...createValuesMapping(BorderColorAliases, value => `${BorderWidthAndStyle} var(${normalizeVariable(value, composePrefixes(ColorPrefix, "b"))})`),
  ...createValuesMapping(Colors, value => `${BorderWidthAndStyle} var(${normalizeVariable(value, ColorPrefix)})`)
};
export const BorderRadiusMapping = createValuesMapping(BorderRadiusScale, createPrefixedValueTemplate(BorderRadiusPrefix));
export const BoxShadowMapping = { ...createValuesMapping(BoxShadowScale, createPrefixedValueTemplate(BoxShadowPrefix)),
  ...createValuesMapping(BoxShadowAliases, createPrefixedValueTemplate(BoxShadowPrefix))
};
export const FontSizeMapping = createValuesMapping(FontSizeScale, createPrefixedValueTemplate(FontSizePrefix));

const fontVariationSettingsValueTemplate = value => `'wght' var(${normalizeVariable(value, FontWeightPrefix)})`;

export const FontWeightMapping = createValuesMapping(FontWeightScale, fontVariationSettingsValueTemplate);
export const IconColorMapping = { ...createValuesMapping(IconColorAliases, createPrefixedValueTemplate(composePrefixes(ColorPrefix, "icon"))),
  ...ColorMapping
};
export const LineHeightMapping = createValuesMapping(LineHeightScale, createPrefixedValueTemplate(LineHeightPrefix));
export const TextColorMapping = { ...createValuesMapping(TextColorAliases, createPrefixedValueTemplate(composePrefixes(ColorPrefix, "text"))),
  ...ColorMapping
};

class StylingContext {
  constructor(className, style, matchedBreakpoints) {
    this.classes = void 0;
    this.style = void 0;
    this.matchedBreakpoints = void 0;
    this.classes = !isNil(className) ? [className] : [];
    this.style = style !== null && style !== void 0 ? style : {};
    this.matchedBreakpoints = matchedBreakpoints;
  }

  addClass(className) {
    if (!this.classes.includes(className)) {
      this.classes.push(className);
    }
  }

  addStyleValue(name, value) {
    if (isNil(this.style[name])) {
      this.style[name] = value;
    }
  }

  computeStyling() {
    const className = this.classes.length !== 0 ? this.classes.join(" ") : undefined;
    const styleValue = Object.keys(this.style).length !== 0 ? this.style : undefined;
    return {
      className,
      style: styleValue
    };
  }

}

function getSystemValue(value, systemValues) {
  return systemValues[value];
}

export function getSizingValue(value) {
  const systemValue = getSystemValue(value, SizingMapping);
  return systemValue !== null && systemValue !== void 0 ? systemValue : value;
}

function parseResponsiveSystemValue(value, systemValues, matchedBreakpoints) {
  if (isNil(value)) {
    return value;
  } // Quick lookup since most values will be a non responsive system value and will not requires to parse for a responsive value.


  const systemValue = getSystemValue(value, systemValues);

  if (!isNil(systemValue)) {
    return systemValue;
  }

  const underlyingValue = parseResponsiveValue(value, matchedBreakpoints);

  if (!isNil(underlyingValue)) {
    const underlyingSystemValue = getSystemValue(underlyingValue, systemValues);

    if (!isNil(underlyingSystemValue)) {
      return underlyingSystemValue;
    }
  }

  return underlyingValue;
}

function createHandler(systemValues) {
  const systemValueHandler = (name, value, context) => {
    const parsedValue = parseResponsiveSystemValue(value, systemValues, context.matchedBreakpoints);

    if (!isNil(parsedValue)) {
      context.addStyleValue(name, parsedValue);
    }
  };

  const passThroughHandler = (name, value, context) => {
    const parsedValue = parseResponsiveValue(value, context.matchedBreakpoints);

    if (!isNil(parsedValue)) {
      context.addStyleValue(name, parsedValue);
    }
  };

  return !isNil(systemValues) ? systemValueHandler : passThroughHandler;
}

function createPseudoHandler(pseudoClassName, pseudoVariable, systemValues) {
  const systemValueHandler = (name, value, context) => {
    const parsedValue = parseResponsiveSystemValue(value, systemValues, context.matchedBreakpoints);

    if (!isNil(parsedValue)) {
      context.addClass(pseudoClassName);
      context.addStyleValue(pseudoVariable, parsedValue);
    }
  };

  const passThroughHandler = (name, value, context) => {
    const parsedValue = parseResponsiveValue(value, context.matchedBreakpoints);

    if (!isNil(parsedValue)) {
      context.addClass(pseudoClassName);
      context.addStyleValue(pseudoVariable, parsedValue);
    }
  };

  return !isNil(systemValues) ? systemValueHandler : passThroughHandler;
} // Custom handler for borders to allow the following syntax:
// - border="sunray-10" -> style="1px solid var(--o-ui-sunray-10)"
// - border="hsla(223, 12%, 87%, 1)" -> style="1px solid hsla(223, 12%, 87%, 1)"


function createBorderHandler(systemValues) {
  return (name, value, context) => {
    const parsedValue = parseResponsiveSystemValue(value, systemValues, context.matchedBreakpoints);

    if (!isNil(parsedValue)) {
      if (ColorExpressionTypes.some(x => parsedValue.startsWith(x))) {
        context.addStyleValue(name, `${BorderWidthAndStyle} ${parsedValue}`);
      } else {
        context.addStyleValue(name, parsedValue);
      }
    }
  };
}

function createBorderPseudoHandler(pseudoClassName, pseudoVariable, systemValues) {
  return (name, value, context) => {
    const parsedValue = parseResponsiveSystemValue(value, systemValues, context.matchedBreakpoints);

    if (!isNil(parsedValue)) {
      context.addClass(pseudoClassName);

      if (ColorExpressionTypes.some(x => parsedValue.startsWith(x))) {
        context.addStyleValue(pseudoVariable, `${BorderWidthAndStyle} ${parsedValue}`);
      } else {
        context.addStyleValue(pseudoVariable, parsedValue);
      }
    }
  };
} // Custom handler for font-weight because of "fontVariationSettings".


const fontWeightHandler = (name, value, context) => {
  const parsedValue = parseResponsiveSystemValue(value, FontWeightMapping, context.matchedBreakpoints);

  if (!isNil(parsedValue)) {
    context.addStyleValue("fontVariationSettings", parsedValue);

    if (!GlobalValues.includes(parsedValue)) {
      context.addStyleValue("fontWeight", "400");
    }
  }
};

const gridColumnSpanHandler = (name, value, context) => {
  const parsedValue = parseResponsiveValue(value, context.matchedBreakpoints);

  if (!isNil(parsedValue)) {
    context.addStyleValue("gridColumn", `span ${parsedValue} / span ${parsedValue}`);
  }
};

const gridRowSpanHandler = (name, value, context) => {
  const parsedValue = parseResponsiveValue(value, context.matchedBreakpoints);

  if (!isNil(parsedValue)) {
    context.addStyleValue("gridRow", `span ${parsedValue} / span ${parsedValue}`);
  }
};

function createAxisHandler(firstPropName, secondPropName, systemValues) {
  const firstHandler = createHandler(systemValues);
  const secondHandler = createHandler(systemValues);
  return (name, value, context) => {
    firstHandler(firstPropName, value, context);
    secondHandler(secondPropName, value, context);
  };
}

const PropsHandlers = {
  alignContent: createHandler(),
  alignItems: createHandler(),
  alignSelf: createHandler(),
  aspectRatio: createHandler(),
  backgroundColor: createHandler(BackgroundColorMapping),
  backgroundColorActive: createPseudoHandler("o-ui-bg-active", "--o-ui-bg-active", BackgroundColorMapping),
  backgroundColorFocus: createPseudoHandler("o-ui-bg-focus", "--o-ui-bg-focus", BackgroundColorMapping),
  backgroundColorHover: createPseudoHandler("o-ui-bg-hover", "--o-ui-bg-hover", BackgroundColorMapping),
  backgroundImage: createHandler(),
  backgroundPosition: createHandler(),
  backgroundRepeat: createHandler(),
  backgroundSize: createHandler(),
  border: createBorderHandler(BorderMapping),
  borderBottom: createBorderHandler(BorderMapping),
  borderBottomActive: createBorderPseudoHandler("o-ui-bb-active", "--o-ui-bb-active", BorderMapping),
  borderBottomFocus: createBorderPseudoHandler("o-ui-bb-focus", "--o-ui-bb-focus", BorderMapping),
  borderBottomHover: createBorderPseudoHandler("o-ui-bb-hover", "--o-ui-bb-hover", BorderMapping),
  borderBottomLeftRadius: createHandler(BorderRadiusMapping),
  borderBottomRightRadius: createHandler(BorderRadiusMapping),
  borderActive: createBorderPseudoHandler("o-ui-b-active", "--o-ui-b-active", BorderMapping),
  borderFocus: createBorderPseudoHandler("o-ui-b-focus", "--o-ui-b-focus", BorderMapping),
  borderHover: createBorderPseudoHandler("o-ui-b-hover", "--o-ui-b-hover", BorderMapping),
  borderLeft: createBorderHandler(BorderMapping),
  borderLeftActive: createBorderPseudoHandler("o-ui-bl-active", "--o-ui-bl-active", BorderMapping),
  borderLeftFocus: createBorderPseudoHandler("o-ui-bl-focus", "--o-ui-bl-focus", BorderMapping),
  borderLeftHover: createBorderPseudoHandler("o-ui-bl-hover", "--o-ui-bl-hover", BorderMapping),
  borderRadius: createHandler(BorderRadiusMapping),
  borderRight: createBorderHandler(BorderMapping),
  borderRightActive: createBorderPseudoHandler("o-ui-br-active", "--o-ui-br-active", BorderMapping),
  borderRightFocus: createBorderPseudoHandler("o-ui-br-focus", "--o-ui-br-focus", BorderMapping),
  borderRightHover: createBorderPseudoHandler("o-ui-br-hover", "--o-ui-br-hover", BorderMapping),
  borderTop: createBorderHandler(BorderMapping),
  borderTopActive: createBorderPseudoHandler("o-ui-bt-active", "--o-ui-bt-active", BorderMapping),
  borderTopFocus: createBorderPseudoHandler("o-ui-bt-focus", "--o-ui-bt-focus", BorderMapping),
  borderTopHover: createBorderPseudoHandler("o-ui-bt-hover", "--o-ui-bt-hover", BorderMapping),
  borderTopLeftRadius: createHandler(BorderRadiusMapping),
  borderTopRightRadius: createHandler(BorderRadiusMapping),
  bottom: createHandler(),
  boxShadow: createHandler(BoxShadowMapping),
  boxShadowActive: createPseudoHandler("o-ui-bs-active", "--o-ui-bs-active", BoxShadowMapping),
  boxShadowFocus: createPseudoHandler("o-ui-bs-focus", "--o-ui-bs-focus", BoxShadowMapping),
  boxShadowHover: createPseudoHandler("o-ui-bs-hover", "--o-ui-bs-hover", BoxShadowMapping),
  color: createHandler(TextColorMapping),
  colorActive: createPseudoHandler("o-ui-c-active", "--o-ui-c-active", TextColorMapping),
  colorFocus: createPseudoHandler("o-ui-c-focus", "--o-ui-c-focus", TextColorMapping),
  colorHover: createPseudoHandler("o-ui-c-hover", "--o-ui-c-hover", TextColorMapping),
  columnGap: createHandler(SpacingMapping),
  content: createHandler(),
  contentVisibility: createHandler(),
  cursor: createHandler(),
  cursorHover: createPseudoHandler("o-ui-cs-hover", "--o-ui-cs-hover"),
  display: createHandler(),
  fill: createHandler(IconColorMapping),
  fillFocus: createPseudoHandler("o-ui-f-focus", "--o-ui-f-focus", BorderMapping),
  fillHover: createPseudoHandler("o-ui-f-hover", "--o-ui-f-hover", BorderMapping),
  filter: createHandler(),
  flex: createHandler(),
  flexBasis: createHandler(),
  flexDirection: createHandler(),
  flexFlow: createHandler(),
  flexGrow: createHandler(),
  flexShrink: createHandler(),
  flexWrap: createHandler(),
  fontSize: createHandler(FontSizeMapping),
  fontStyle: createHandler(),
  fontWeight: fontWeightHandler,
  gap: createHandler(SpacingMapping),
  grid: createHandler(),
  gridArea: createHandler(),
  gridAutoColumns: createHandler(SizingMapping),
  gridAutoFlow: createHandler(),
  gridAutoRows: createHandler(SizingMapping),
  gridColumn: createHandler(),
  gridColumnEnd: createHandler(),
  gridColumnSpan: gridColumnSpanHandler,
  gridColumnStart: createHandler(),
  gridRow: createHandler(),
  gridRowEnd: createHandler(),
  gridRowSpan: gridRowSpanHandler,
  gridRowStart: createHandler(),
  gridTemplate: createHandler(),
  gridTemplateAreas: createHandler(),
  gridTemplateColumns: createHandler(SizingMapping),
  gridTemplateRows: createHandler(SizingMapping),
  height: createHandler(SizingMapping),
  justifyContent: createHandler(),
  justifyItems: createHandler(),
  justifySelf: createHandler(),
  left: createHandler(),
  letterSpacing: createHandler(),
  lineHeight: createHandler(LineHeightMapping),
  margin: createHandler(SpacingMapping),
  marginBottom: createHandler(SpacingMapping),
  marginLeft: createHandler(SpacingMapping),
  marginRight: createHandler(SpacingMapping),
  marginTop: createHandler(SpacingMapping),
  marginX: createAxisHandler("marginLeft", "marginRight", SpacingMapping),
  marginY: createAxisHandler("marginBottom", "marginTop", SpacingMapping),
  maxHeight: createHandler(SizingMapping),
  maxWidth: createHandler(SizingMapping),
  minHeight: createHandler(SizingMapping),
  minWidth: createHandler(SizingMapping),
  objectFit: createHandler(),
  objectPosition: createHandler(),
  opacity: createHandler(),
  opacityActive: createPseudoHandler("o-ui-o-active", "o-ui-o-active"),
  opacityFocus: createPseudoHandler("o-ui-o-focus", "o-ui-o-focus"),
  opacityHover: createPseudoHandler("o-ui-o-hover", "o-ui-o-hover"),
  order: createHandler(),
  outline: createHandler(),
  outlineFocus: createPseudoHandler("o-ui-ol-focus", "o-ui-ol-focus"),
  overflow: createHandler(),
  overflowX: createHandler(),
  overflowY: createHandler(),
  padding: createHandler(SpacingMapping),
  paddingBottom: createHandler(SpacingMapping),
  paddingLeft: createHandler(SpacingMapping),
  paddingRight: createHandler(SpacingMapping),
  paddingTop: createHandler(SpacingMapping),
  paddingX: createAxisHandler("paddingLeft", "paddingRight", SpacingMapping),
  paddingY: createAxisHandler("paddingBottom", "paddingTop", SpacingMapping),
  pointerEvents: createHandler(),
  position: createHandler(),
  resize: createHandler(),
  right: createHandler(),
  rowGap: createHandler(SpacingMapping),
  stroke: createHandler(IconColorMapping),
  textAlign: createHandler(),
  textDecoration: createHandler(),
  textOverflow: createHandler(),
  textTransform: createHandler(),
  top: createHandler(),
  transform: createHandler(),
  transformOrigin: createHandler(),
  transformStyle: createHandler(),
  verticalAlign: createHandler(),
  visibility: createHandler(),
  whiteSpace: createHandler(),
  width: createHandler(SizingMapping),
  willChange: createHandler(),
  wordBreak: createHandler(),
  zIndex: createHandler()
};
export function useStyledSystem(props) {
  const {
    alignContent,
    alignItems,
    alignSelf,
    aspectRatio,
    backgroundColor,
    backgroundColorActive,
    backgroundColorFocus,
    backgroundColorHover,
    backgroundImage,
    backgroundPosition,
    backgroundRepeat,
    backgroundSize,
    border,
    borderBottom,
    borderBottomActive,
    borderBottomFocus,
    borderBottomHover,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    borderFocus,
    borderHover,
    borderActive,
    borderLeft,
    borderLeftActive,
    borderLeftFocus,
    borderLeftHover,
    borderRadius,
    borderRight,
    borderRightActive,
    borderRightFocus,
    borderRightHover,
    borderTop,
    borderTopActive,
    borderTopFocus,
    borderTopHover,
    borderTopLeftRadius,
    borderTopRightRadius,
    bottom,
    boxShadow,
    boxShadowActive,
    boxShadowFocus,
    boxShadowHover,
    className,
    color,
    colorActive,
    colorFocus,
    colorHover,
    columnGap,
    content,
    contentVisibility,
    cursor,
    cursorHover,
    display,
    fill,
    fillFocus,
    fillHover,
    filter,
    flex,
    flexBasis,
    flexDirection,
    flexFlow,
    flexGrow,
    flexShrink,
    flexWrap,
    fontSize,
    fontStyle,
    fontWeight,
    gap,
    grid,
    gridArea,
    gridAutoColumns,
    gridAutoFlow,
    gridAutoRows,
    gridColumn,
    gridColumnEnd,
    gridColumnSpan,
    gridColumnStart,
    gridRow,
    gridRowEnd,
    gridRowSpan,
    gridRowStart,
    gridTemplate,
    gridTemplateAreas,
    gridTemplateColumns,
    gridTemplateRows,
    height,
    justifyContent,
    justifyItems,
    justifySelf,
    left,
    letterSpacing,
    lineHeight,
    margin,
    marginBottom,
    marginLeft,
    marginRight,
    marginTop,
    marginX,
    marginY,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    objectFit,
    objectPosition,
    opacity,
    opacityActive,
    opacityFocus,
    opacityHover,
    order,
    outline,
    outlineFocus,
    overflow,
    overflowX,
    overflowY,
    padding,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingX,
    paddingY,
    pointerEvents,
    position,
    resize,
    right,
    rowGap,
    stroke,
    style,
    textAlign,
    textDecoration,
    textOverflow,
    textTransform,
    top,
    transform,
    transformOrigin,
    transformStyle,
    verticalAlign,
    visibility,
    whiteSpace,
    width,
    willChange,
    wordBreak,
    zIndex,
    ...rest
  } = props;
  const matchedBreakpoints = useMatchedBreakpoints(); // We don't have to add "props" as a dependency because useStyledSystem return the "rest" which is all the props that are not already a dependency
  // of this memoization. If we do add props, the memoization will refresh on every render, which is bad, so don't do it.

  /* eslint-disable react-hooks/exhaustive-deps */

  const styling = useMemo(() => {
    const context = new StylingContext(className, style, matchedBreakpoints);
    Object.keys(props).forEach(key => {
      const value = props[key];

      if (!isNil(value)) {
        const handler = PropsHandlers[key];

        if (!isNil(handler)) {
          handler(key, value, context);
        }
      }
    });
    return context.computeStyling();
  }, [alignContent, alignItems, alignSelf, aspectRatio, backgroundColor, backgroundColorActive, backgroundColorFocus, backgroundColorHover, backgroundImage, backgroundPosition, backgroundRepeat, backgroundSize, border, borderBottom, borderBottomActive, borderBottomFocus, borderBottomHover, borderLeft, borderLeftActive, borderLeftFocus, borderLeftHover, borderRight, borderRightActive, borderRightFocus, borderRightHover, borderTop, borderTopActive, borderTopFocus, borderTopHover, borderActive, borderFocus, borderHover, borderRadius, borderBottomLeftRadius, borderBottomRightRadius, borderTopLeftRadius, borderTopRightRadius, boxShadow, boxShadowActive, boxShadowFocus, boxShadowHover, bottom, matchedBreakpoints, className, color, colorActive, colorFocus, colorHover, columnGap, content, contentVisibility, cursor, cursorHover, display, fill, fillFocus, fillHover, filter, flex, flexBasis, flexDirection, flexFlow, flexGrow, flexShrink, flexWrap, fontSize, fontStyle, fontWeight, gap, grid, gridArea, gridAutoColumns, gridAutoFlow, gridAutoRows, gridColumn, gridColumnEnd, gridColumnSpan, gridColumnStart, gridRow, gridRowEnd, gridRowSpan, gridRowStart, gridTemplate, gridTemplateAreas, gridTemplateColumns, gridTemplateRows, height, justifyContent, justifyItems, justifySelf, left, letterSpacing, lineHeight, margin, marginBottom, marginLeft, marginRight, marginTop, marginX, marginY, maxHeight, maxWidth, minHeight, minWidth, objectFit, objectPosition, opacity, opacityActive, opacityFocus, opacityHover, order, outline, outlineFocus, overflow, overflowX, overflowY, padding, paddingBottom, paddingLeft, paddingRight, paddingTop, paddingX, paddingY, pointerEvents, position, resize, right, rowGap, stroke, style, textAlign, textDecoration, textOverflow, textTransform, top, transform, transformOrigin, transformStyle, verticalAlign, visibility, whiteSpace, width, willChange, wordBreak, zIndex]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return { ...rest,
    className: styling.className,
    style: styling.style
  };
}